import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    getToken,
    removeToken,
    saveToken,
    saveAuthData,
    getAuthData,
    saveRefreshToken,
    getRefreshToken,
    removeTempToken,
    isTokenExpired,
    isCookieAuthEnabled,
} from "@/shared/lib/token";
import { useRouter } from "@/shared/lib/router/router";
import { AuthResponse } from "@/shared/types/auth";
import { UserGetDTO } from "@/shared/types/shared/me";
import { authService } from "@/entities/session/api/authService";
import { refreshSession } from "@/shared/api/client";
import { authContext } from "@/shared/model/authContext";
import { queryClient } from "@/shared/lib/queryClient";
import { endSupportSession, getSupportSession, isReadOnlyPermission } from "@/shared/lib/supportSession";
import { hasPlatformAdminClaim } from "@/shared/lib/tokenClaims";

export function AuthProvider({ children }: { children: React.ReactNode }) {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [loading, setLoading] = useState(true);
    const [userPermissions, setUserPermissions] = useState<string[]>([]);
    const [userRoles, setUserRoles] = useState<string[]>([]);
    const router = useRouter();

    const [user, setUser] = useState<AuthResponse | null>(null);
    const [userProfile, setUserProfile] = useState<UserGetDTO | null>(null);
    const [avatarUrl, setAvatarUrl] = useState<string>("");
    const [isSupportSession, setIsSupportSession] = useState(false);

    const signOut = useCallback(() => {
        // Sair de uma sessão de suporte é voltar à do admin, não deslogá-lo.
        const supportReturnUrl = endSupportSession();
        if (supportReturnUrl) {
            queryClient.clear();
            window.location.assign(supportReturnUrl);
            return;
        }

        const refreshToken = getRefreshToken();

        // Clear local state first to prevent loops
        removeToken();
        removeTempToken();
        queryClient.clear();
        setIsAuthenticated(false);
        setUserPermissions([]);
        setUserRoles([]);
        setAvatarUrl("");
        setUser(null);
        setUserProfile(null);

        // Redirect immediately
        if (router.pathname !== "/login") {
            router.replace("/login");
        }

        // Attempt backend logout, but don't wait for it or crash if it fails
        if (refreshToken) {
            authService.logout(refreshToken).catch((error) => {
                console.warn("[AuthContext] Backend logout failed, but local session was cleared:", error);
            });
        }
    }, [router]);

    const updateUserProfile = useCallback(async () => {
        try {
            // Lazy load profile image and data
            const { meService } = await import("@/entities/user/@x/session");

            const profilePromise = meService.getMe();
            const imagePromise = meService.getProfileImage().catch(() => "");

            try {
                const profileData = await profilePromise;
                if (profileData) {
                    setUserProfile(profileData);
                }
            } catch (error) {
                console.error("Erro ao carregar dados do usuário. Sessão inválida?", error);
                const status = (error as { response?: { status?: number } })?.response?.status;
                // Só encerra a sessão se o backend responder que as credenciais são inválidas (401/403).
                // Erros transitórios de rede ou 5xx do servidor não devem limpar o login do usuário.
                if (status === 401 || status === 403) {
                    signOut();
                }
                return;
            }

            const image = await imagePromise;
            if (image) setAvatarUrl(image);

        } catch (error) {
            console.error("Erro crítico ao atualizar perfil:", error);
        }
    }, [signOut]);

    // A inicialização roda uma vez na montagem; o ref entrega a versão atual
    // sem fazer o efeito depender da identidade do callback (que muda com o router).
    const updateUserProfileRef = useRef(updateUserProfile);
    useEffect(() => {
        updateUserProfileRef.current = updateUserProfile;
    }, [updateUserProfile]);

    useEffect(() => {
        const initAuth = async () => {
            setIsSupportSession(getSupportSession() !== null);
            const token = getToken();
            const refreshToken = getRefreshToken();

            if (token && !isTokenExpired(token)) {
                const { permissions, roles } = getAuthData();
                setUserPermissions(permissions);
                setUserRoles(roles);
                setIsAuthenticated(true);
                updateUserProfileRef.current();
            } else if (refreshToken || isCookieAuthEnabled()) {
                // Token ausente/expirado, mas há refresh token disponível
                // (ou modo cookie, onde o refresh token vive num cookie HttpOnly):
                // tenta renovar antes de deslogar — usando a renovação única
                // compartilhada (mesma trava do interceptor → sem corrida).
                try {
                    const accessToken = await refreshSession();

                    if (accessToken) {
                        const { permissions, roles } = getAuthData();
                        setUserPermissions(permissions);
                        setUserRoles(roles);
                        setIsAuthenticated(true);
                        updateUserProfileRef.current();
                    } else {
                        throw new Error("Invalid token format");
                    }
                } catch (error) {
                    removeToken();
                    removeTempToken();
                    setIsAuthenticated(false);
                    setUserPermissions([]);
                    setUserRoles([]);
                }
            } else if (token) {
                // Token expirado e sem refresh token disponível
                removeToken();
                removeTempToken();
                setIsAuthenticated(false);
                setUserPermissions([]);
                setUserRoles([]);
            }

            setLoading(false);
        };

        initAuth();
    }, []);

    const signIn = useCallback((data: AuthResponse) => {
        saveToken(data.accessToken);
        if (data.refreshToken) {
            saveRefreshToken(data.refreshToken);
        }
        saveAuthData(data.permissions, data.roles);

        setUserPermissions(data.permissions);
        setUserRoles(data.roles);
        setIsAuthenticated(true);
        updateUserProfile();

        router.push("/");
    }, [router, updateUserProfile]);

    // O token só muda junto com isAuthenticated: login, logout e o arranque. Trocar de
    // organização e entrar numa sessão de suporte recarregam a página.
    const isPlatformAdmin = useMemo(() => isAuthenticated && hasPlatformAdminClaim(), [isAuthenticated]);

    const checkPermission = useCallback((permission: string): boolean => {
        if (isSupportSession && !isReadOnlyPermission(permission)) {
            return false;
        }
        return userPermissions.includes(permission);
    }, [userPermissions, isSupportSession]);

    const contextValue = useMemo(() => ({
        isAuthenticated,
        loading,
        signIn,
        signOut,
        userPermissions,
        userRoles,
        checkPermission,
        user,
        avatarUrl,
        userProfile,
        updateUserProfile,
        isSupportSession,
        isPlatformAdmin,
    }), [isAuthenticated, loading, signIn, signOut, userPermissions, userRoles, checkPermission, user, avatarUrl, userProfile, updateUserProfile, isSupportSession, isPlatformAdmin]);

    return (
        <authContext.Provider value={contextValue}>
            {children}
        </authContext.Provider>
    );
}